import { useEffect, useRef, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Button } from "./components/ui/button";
import { Switch } from "./components/ui/switch";
import { Slider } from "./components/ui/slider";
import { Textarea } from "./components/ui/textarea";
import { SignPreview } from "./components/SignPreview";
import {
  Plus,
  Save,
  Printer,
  Copy,
  Trash2,
  FileText,
  Tag,
  Menu,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MM_TO_PX = 96 / 25.4;
const PAPER_W = 210 * MM_TO_PX;
const PAPER_H = 297 * MM_TO_PX;

const DEFAULT_SIGN = {
  product_name: "Jack Daniels BBQ Sauce",
  price_label: "PRIS PR. STK",
  price_whole: "49",
  price_decimal: "99",
  bar_text: "",
  date_label: "BEDST FØR",
  date_value: "30.03.2026",
  date_size: 8,
  show_date: true,
  show_bar: true,
};

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
        {label}
      </Label>
      {children}
    </div>
  );
}

function App() {
  const [signs, setSigns] = useState([]);
  const [current, setCurrent] = useState(DEFAULT_SIGN);
  const [currentId, setCurrentId] = useState(null);
  const [scale, setScale] = useState(0.5);
  const [saving, setSaving] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const canvasRef = useRef(null);

  const inputCls =
    "bg-zinc-900 border-zinc-800 rounded-none h-9 text-sm text-white focus-visible:ring-1 focus-visible:ring-zinc-500 focus-visible:ring-offset-0";

  const fetchSigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/signs`);
      setSigns(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchSigns();
  }, [fetchSigns]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const compute = () => {
      const availW = el.clientWidth - 40;
      const availH = el.clientHeight - 40;
      const s = Math.min(availW / PAPER_W, availH / PAPER_H);
      setScale(Math.max(0.1, s));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const update = (field, value) =>
    setCurrent((prev) => ({ ...prev, [field]: value }));

  const newSign = () => {
    setCurrent(DEFAULT_SIGN);
    setCurrentId(null);
    toast("Nyt skilt", { description: "Tomt prisskilt klar til redigering." });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (currentId) {
        const res = await axios.put(`${API}/signs/${currentId}`, current);
        setCurrent(res.data);
        toast.success("Skilt gemt");
      } else {
        const res = await axios.post(`${API}/signs`, current);
        setCurrentId(res.data.id);
        setCurrent(res.data);
        toast.success("Skilt gemt");
      }
      fetchSigns();
    } catch (e) {
      toast.error("Kunne ikke gemme skiltet");
    } finally {
      setSaving(false);
    }
  };

  const loadSign = (s) => {
    setCurrent(s);
    setCurrentId(s.id);
    setSavedOpen(false);
  };

  const duplicate = async (id, e) => {
    e.stopPropagation();
    try {
      const res = await axios.post(`${API}/signs/${id}/duplicate`);
      await fetchSigns();
      loadSign(res.data);
      toast.success("Skilt kopieret");
    } catch (e) {
      toast.error("Kunne ikke kopiere");
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/signs/${id}`);
      if (currentId === id) newSign();
      fetchSigns();
      toast.success("Skilt slettet");
    } catch (e) {
      toast.error("Kunne ikke slette");
    }
  };

  const print = () => window.print();

  const savedList = (
    <div
      className="flex-1 overflow-y-auto thin-scroll p-2 space-y-1"
      data-testid="saved-signs-list"
    >
      {signs.length === 0 && (
        <div className="text-xs text-zinc-600 px-2 py-6 text-center">
          Ingen skilte endnu.<br />Lav et og tryk "Gem".
        </div>
      )}
      {signs.map((s) => (
        <div
          key={s.id}
          onClick={() => loadSign(s)}
          data-testid={`saved-sign-${s.id}`}
          className={`group cursor-pointer px-3 py-2.5 border-l-2 transition-colors ${
            currentId === s.id
              ? "bg-white/5 border-l-yellow-400"
              : "border-l-transparent hover:bg-white/5"
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate text-white">
                {s.product_name || "Uden navn"}
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                {s.price_whole},{s.price_decimal} kr
              </div>
            </div>
            <div className="flex gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => duplicate(s.id, e)}
                className="p-1.5 text-zinc-500 hover:text-white"
                data-testid={`duplicate-sign-${s.id}`}
                title="Kopier"
              >
                <Copy className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => remove(s.id, e)}
                className="p-1.5 text-zinc-500 hover:text-red-400"
                data-testid={`delete-sign-${s.id}`}
                title="Slet"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="no-print h-[100dvh] w-screen flex flex-col bg-[var(--shell-bg)] overflow-hidden">
        {/* Header */}
        <header className="h-14 shrink-0 border-b border-zinc-800 bg-[var(--shell-panel)] flex items-center justify-between px-3 sm:px-4 gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <button
              onClick={() => setSavedOpen(true)}
              className="lg:hidden p-1.5 -ml-1 text-zinc-300 hover:text-white"
              data-testid="mobile-saved-toggle"
              title="Gemte skilte"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="h-7 w-7 bg-yellow-400 flex items-center justify-center shrink-0">
              <Tag className="h-4 w-4 text-black" strokeWidth={2.5} />
            </div>
            <div className="font-ui-head font-bold text-[15px] tracking-tight truncate">
              Skilte<span className="text-yellow-400">Studio</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Button
              onClick={newSign}
              variant="ghost"
              className="rounded-none h-9 px-2 sm:px-3 text-zinc-300 hover:bg-white/5 hover:text-white gap-2 text-sm"
              data-testid="new-sign-button"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nyt skilt</span>
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              variant="outline"
              className="rounded-none h-9 px-2 sm:px-3 border-zinc-700 bg-transparent text-white hover:bg-white/5 gap-2 text-sm"
              data-testid="save-sign-button"
            >
              <Save className="h-4 w-4" />
              <span className="hidden sm:inline">{currentId ? "Gem" : "Gem nyt"}</span>
            </Button>
            <Button
              onClick={print}
              className="rounded-none h-9 px-2 sm:px-3 bg-white text-black hover:bg-zinc-200 gap-2 text-sm font-semibold"
              data-testid="print-sign-button"
            >
              <Printer className="h-4 w-4" />
              <span className="hidden sm:inline">Print</span>
            </Button>
          </div>
        </header>

        <div className="flex-1 flex flex-col lg:flex-row min-h-0">
          {/* Left: saved signs (desktop) */}
          <aside className="hidden lg:flex w-64 shrink-0 border-r border-zinc-800 bg-[var(--shell-panel)] flex-col">
            <div className="h-11 shrink-0 flex items-center px-4 border-b border-zinc-800">
              <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                Gemte skilte ({signs.length})
              </span>
            </div>
            {savedList}
          </aside>

          {/* Saved signs drawer (mobile) */}
          {savedOpen && (
            <div className="lg:hidden fixed inset-0 z-50 flex">
              <div
                className="absolute inset-0 bg-black/60"
                onClick={() => setSavedOpen(false)}
              />
              <div className="relative w-72 max-w-[82%] bg-[var(--shell-panel)] border-r border-zinc-800 flex flex-col">
                <div className="h-12 shrink-0 flex items-center justify-between px-4 border-b border-zinc-800">
                  <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                    Gemte skilte ({signs.length})
                  </span>
                  <button
                    onClick={() => setSavedOpen(false)}
                    className="p-1 text-zinc-400 hover:text-white"
                    data-testid="mobile-saved-close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                {savedList}
              </div>
            </div>
          )}

          {/* Center: canvas */}
          <main
            ref={canvasRef}
            className="min-h-[42vh] h-[42vh] lg:h-auto lg:flex-1 min-w-0 bg-[#0b0b0d] flex items-center justify-center overflow-auto relative shrink-0 lg:shrink"
            style={{
              backgroundImage:
                "radial-gradient(#1c1c1f 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            }}
          >
            <div
              className="relative shadow-[0_0_60px_rgba(0,0,0,0.6)]"
              style={{ width: PAPER_W * scale, height: PAPER_H * scale }}
            >
              <SignPreview
                sign={current}
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              />
            </div>
          </main>

          {/* Right: editor form */}
          <aside className="w-full lg:w-80 flex-1 lg:flex-none min-h-0 shrink-0 border-t lg:border-t-0 lg:border-l border-zinc-800 bg-[var(--shell-panel)] flex flex-col">
            <div className="h-11 shrink-0 flex items-center px-4 border-b border-zinc-800 gap-2">
              <FileText className="h-3.5 w-3.5 text-zinc-500" />
              <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                Rediger skilt
              </span>
            </div>
            <div className="flex-1 overflow-y-auto thin-scroll p-4 space-y-5">
              <Field label="Produktnavn">
                <Input
                  className={inputCls}
                  value={current.product_name}
                  onChange={(e) => update("product_name", e.target.value)}
                  data-testid="editor-product-name-input"
                />
              </Field>

              <Field label="Pris-tekst">
                <Input
                  className={inputCls}
                  value={current.price_label}
                  onChange={(e) => update("price_label", e.target.value)}
                  data-testid="editor-price-label-input"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Pris (hele)">
                  <Input
                    className={inputCls}
                    value={current.price_whole}
                    onChange={(e) => update("price_whole", e.target.value)}
                    data-testid="editor-price-whole-input"
                  />
                </Field>
                <Field label="Øre">
                  <Input
                    className={inputCls}
                    value={current.price_decimal}
                    onChange={(e) => update("price_decimal", e.target.value)}
                    data-testid="editor-price-decimal-input"
                  />
                </Field>
              </div>

              <div className="h-px bg-zinc-800" />

              <div className="flex items-center justify-between">
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
                  Sort bjælke
                </Label>
                <Switch
                  checked={current.show_bar}
                  onCheckedChange={(v) => update("show_bar", v)}
                  data-testid="editor-show-bar-switch"
                />
              </div>
              {current.show_bar && (
                <Field label="Tekst i sort boks (flere linjer tilladt)">
                  <Textarea
                    className={`${inputCls} h-auto min-h-[56px] py-2 leading-snug`}
                    rows={2}
                    value={current.bar_text}
                    onChange={(e) => update("bar_text", e.target.value)}
                    placeholder={"fx SÆLGES KUN I HELE RAMMER"}
                    data-testid="editor-bar-text-input"
                  />
                </Field>
              )}

              <div className="h-px bg-zinc-800" />

              <div className="flex items-center justify-between">
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
                  Dato
                </Label>
                <Switch
                  checked={current.show_date}
                  onCheckedChange={(v) => update("show_date", v)}
                  data-testid="editor-show-date-switch"
                />
              </div>
              {current.show_date && (
                <>
                  <Field label="Dato-tekst">
                    <Input
                      className={inputCls}
                      value={current.date_label}
                      onChange={(e) => update("date_label", e.target.value)}
                      data-testid="editor-date-label-input"
                    />
                  </Field>
                  <Field label="Dato / ekstra tekst (flere linjer tilladt)">
                    <Textarea
                      className={`${inputCls} h-auto min-h-[64px] py-2 leading-snug`}
                      rows={2}
                      value={current.date_value}
                      onChange={(e) => update("date_value", e.target.value)}
                      placeholder={"30.03.2026\nPriserne er ex. pant"}
                      data-testid="editor-date-value-input"
                    />
                    <p className="text-[10px] text-zinc-600 mt-1">
                      Tryk Enter for at lave en ny linje.
                    </p>
                  </Field>
                </>
              )}
              {current.show_date && (
                <Field label={`Tekststørrelse (${current.date_size ?? 8} mm)`}>
                  <Slider
                    min={4}
                    max={20}
                    step={0.5}
                    value={[current.date_size ?? 8]}
                    onValueChange={(v) => update("date_size", v[0])}
                    className="py-2"
                    data-testid="editor-date-size-slider"
                  />
                </Field>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Print-only sign */}
      <div className="print-container">
        <SignPreview sign={current} className="print-only-canvas" />
      </div>

      <Toaster theme="dark" position="bottom-right" />
    </>
  );
}

export default App;
